import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Ticket, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import AdminShell from "../components/AdminShell";
import { api } from "../lib/api";

const INPUT = "rounded-md border border-neutral-700 bg-neutral-950 px-2.5 py-1.5 text-xs text-white outline-none focus:border-[#0A8B85]";
const fmt = (ts) => (ts ? new Date(ts).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");

const CopyBtn = ({ code }) => {
  const [ok, setOk] = useState(false);
  return (
    <button type="button" onClick={async () => { await navigator.clipboard.writeText(code); setOk(true); setTimeout(() => setOk(false), 1500); }}
      className="rounded p-1 text-neutral-500 hover:text-white" aria-label="Copy code" data-testid={`coupon-copy-${code}`}>
      {ok ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
};

export default function AdminCoupons() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ code: "", percent: 25, note: "", max_uses: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/coupons").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load coupons")), []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/admin/coupons", { ...form, percent: Number(form.percent), max_uses: form.max_uses ? Number(form.max_uses) : null });
      toast.success(`Coupon ${form.code.toUpperCase()} created`); setForm({ code: "", percent: 25, note: "", max_uses: "" }); load();
    } catch (ex) { toast.error(ex.response?.data?.detail || "Couldn't create coupon"); }
    finally { setBusy(false); }
  };
  const toggle = async (c) => {
    try { await api.patch(`/admin/coupons/${c.coupon_id}`, { active: !c.active }); load(); }
    catch { toast.error("Couldn't update coupon"); }
  };

  return (
    <AdminShell current="/admin/coupons">
      <div className="mb-6" data-testid="admin-coupons-page">
        <div className="flex items-center gap-2"><Ticket className="h-5 w-5 text-[#E3B84C]" /><h1 className="text-xl font-extrabold text-white">Report coupons</h1></div>
        <p className="mt-1 text-sm text-neutral-400">Discount codes for the Capital Readiness Report unlock. Founders enter them in the "Get your report" checkout; 100% codes unlock instantly with no payment.</p>
      </div>
      <form onSubmit={create} className="mb-6 flex flex-wrap items-end gap-2 rounded-xl border border-neutral-800 bg-neutral-900/60 p-4" data-testid="coupon-create-form">
        <label className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">Code<input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="SUMMER50" className={`mt-1 block w-40 uppercase ${INPUT}`} data-testid="coupon-code-input" /></label>
        <label className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">% off<input type="number" min={1} max={100} required value={form.percent} onChange={(e) => setForm({ ...form, percent: e.target.value })} className={`mt-1 block w-20 ${INPUT}`} data-testid="coupon-percent-input" /></label>
        <label className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">Max uses <span className="normal-case text-neutral-500">(blank = unlimited)</span><input type="number" min={1} value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className={`mt-1 block w-28 ${INPUT}`} data-testid="coupon-max-input" /></label>
        <label className="min-w-[200px] flex-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">Note<input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Who is this for?" className={`mt-1 block w-full ${INPUT}`} data-testid="coupon-note-input" /></label>
        <button type="submit" disabled={busy} className="inline-flex items-center gap-1 rounded-md bg-[#0A8B85] px-3 py-1.5 text-xs font-bold text-white disabled:opacity-40" data-testid="coupon-create-btn">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Create</button>
      </form>
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-[#0A8B85]" /> : (
        <>
          <div className="overflow-x-auto rounded-xl border border-neutral-800" data-testid="coupon-table">
            <table className="w-full text-left text-xs text-neutral-300">
              <thead><tr className="bg-neutral-900 text-[10px] font-bold uppercase tracking-wider text-neutral-400">
                {["Code", "Discount", "Uses", "Note", "Created", "Status"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
              <tbody>{data.coupons.map((c) => (
                <tr key={c.coupon_id} className="border-t border-neutral-800" data-testid={`coupon-row-${c.code}`}>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1 font-mono text-sm font-extrabold text-white">{c.code}<CopyBtn code={c.code} /></span></td>
                  <td className="px-4 py-3"><span className="rounded-full bg-[#E3B84C]/15 px-2 py-0.5 font-bold text-[#E3B84C]">{c.percent}% off</span></td>
                  <td className="px-4 py-3 tabular-nums">{c.uses || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                  <td className="px-4 py-3 text-neutral-400">{c.note || "—"}</td>
                  <td className="px-4 py-3 text-neutral-500">{fmt(c.created_at)}</td>
                  <td className="px-4 py-3">
                    <button type="button" onClick={() => toggle(c)} className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${c.active ? "bg-emerald-500/15 text-emerald-300" : "bg-neutral-700/60 text-neutral-300"}`} data-testid={`coupon-toggle-${c.code}`}>
                      {c.active ? "Active — click to pause" : "Paused — click to activate"}
                    </button>
                  </td>
                </tr>))}</tbody>
            </table>
          </div>
          <h2 className="mt-8 mb-2 text-sm font-extrabold text-white">Recent redemptions</h2>
          {data.redemptions.length === 0 ? <p className="text-xs text-neutral-500" data-testid="coupon-redemptions-empty">No coupons redeemed yet.</p> : (
            <div className="overflow-x-auto rounded-xl border border-neutral-800">
              <table className="w-full text-left text-xs text-neutral-300">
                <thead><tr className="bg-neutral-900 text-[10px] font-bold uppercase tracking-wider text-neutral-400">
                  {["When", "Code", "Founder", "Was", "Paid", "Status"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
                <tbody>{data.redemptions.map((r) => (
                  <tr key={r.redemption_id} className="border-t border-neutral-800">
                    <td className="px-4 py-3 text-neutral-500">{fmt(r.created_at)}</td>
                    <td className="px-4 py-3 font-mono font-bold text-white">{r.code}</td>
                    <td className="px-4 py-3">{r.email}</td>
                    <td className="px-4 py-3 tabular-nums">KES {Number(r.original_amount).toLocaleString()}</td>
                    <td className="px-4 py-3 tabular-nums">{r.final_amount <= 0 ? "Free" : `KES ${Number(r.final_amount).toLocaleString()}`}</td>
                    <td className="px-4 py-3 text-neutral-400">{r.status.replace("_", " ")}</td>
                  </tr>))}</tbody>
              </table>
            </div>
          )}
        </>
      )}
    </AdminShell>
  );
}
